import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Checkbox, Select, selectInvertedStyle } from '@/shared/ui'
import { routes } from '@/shared/config'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'

const SUPPORT_BOT_URL = 'https://t.me/ps24supportbot'

function openSupportChat() {
  window.open(SUPPORT_BOT_URL, '_blank', 'noopener,noreferrer')
}

export type VideosShellSidebarCategory = {
  id: string
  title: string
  urlSlug: string
}

export type VideosShellSidebarBundle = {
  id: string
  title: string
}

export type VideosShellSidebarFilterOption = {
  id: string
  title: string
  count?: string
}

export type VideosShellSidebarFilterSection = {
  id: string
  title: string
  options: VideosShellSidebarFilterOption[]
  selectedOptionIds: string[]
}

export type VideosShellSidebarShortcut = {
  id: 'admin' | 'super-admin'
  label: string
  href: string
}

export type VideosShellSidebarProps = {
  isDesktopViewport: boolean
  isVisible: boolean
  isExpanded: boolean
  isFilterPanelOpen: boolean
  isFilterOnlyVisible?: boolean
  showFilterPanel?: boolean
  activeNavigationItem?: 'home' | 'bookmarks' | 'subscription'
  onNavigationSelect?: (itemId: 'home' | 'bookmarks' | 'subscription') => void
  currentPathname?: string
  shortcuts?: VideosShellSidebarShortcut[]
  categories?: VideosShellSidebarCategory[]
  selectedCategorySlug?: string | null
  onCategorySelect?: (categorySlug: string | null) => void
  bundles?: VideosShellSidebarBundle[]
  selectedBundleId?: string | null
  onBundleSelect?: (bundleId: string | null) => void
  filterSections?: VideosShellSidebarFilterSection[]
  onFilterSelectionChange?: (selectedByFilterId: Record<string, string[]>) => void
  onResetFilters?: () => void
  onLogout?: () => void
}

type FilterOption = {
  id: string
  label: string
  count?: string
  checked?: boolean
}

type FilterSection = {
  id: string
  title: string
  options: FilterOption[]
  footerLabel?: string
}

const sidebarTokens = designTokens.components.videoLibrarySidebar

const railStyle = {
  width: sidebarTokens.railWidth,
  backgroundColor: sidebarTokens.railBackground,
  boxShadow: sidebarTokens.railShadow,
  padding: sidebarTokens.railPadding,
}

const expandedRailStyle = {
  width: sidebarTokens.expandedWidth,
  backgroundColor: sidebarTokens.railBackground,
  boxShadow: sidebarTokens.railShadow,
  padding: sidebarTokens.railPadding,
}

const railNavStyle = {
  width: sidebarTokens.railNavWidth,
  gap: sidebarTokens.railNavGap,
}

const expandedRailNavStyle = {
  width: sidebarTokens.expandedNavWidth,
  gap: sidebarTokens.expandedNavGap,
}

const mobileExpandedRailNavStyle = {
  width: sidebarTokens.expandedNavWidth,
  gap: '12px',
}

const railButtonStyle = {
  width: sidebarTokens.railNavItemWidth,
  height: sidebarTokens.railNavItemHeight,
  borderRadius: sidebarTokens.railNavItemRadius,
}

const expandedRailButtonStyle = {
  width: sidebarTokens.expandedNavWidth,
  height: sidebarTokens.railNavItemHeight,
  borderRadius: sidebarTokens.railNavItemRadius,
}

const mobileExpandedRailButtonStyle = {
  width: sidebarTokens.expandedNavWidth,
  minHeight: '32px',
  height: '32px',
  borderRadius: sidebarTokens.railNavItemRadius,
}

const filterPanelStyle = {
  width: `calc(${sidebarTokens.panelWidth} - 1px)`,
  padding: `0 ${sidebarTokens.panelPaddingX} ${sidebarTokens.panelPaddingY}`,
  borderRight: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${sidebarTokens.panelDivider}`,
  backgroundColor: designTokens.colors.neutral.black,
  boxSizing: 'border-box' as const,
}

const panelBodyStyle = {
  width: sidebarTokens.panelSectionWidth,
  gap: sidebarTokens.panelSectionGap,
}

const sectionTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.mdSemibold),
}

const optionCountStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.additional,
  ...typographyStyle(designTokens.typography.body.base),
}

const navLabelStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  textAlign: 'left' as const,
  ...typographyStyle(designTokens.typography.videoLibrary.sidebarLabel),
}

const mobileNavLabelStyle = {
  ...navLabelStyle,
  ...typographyStyle(designTokens.typography.body.base, {
    fontSize: '15px',
    lineHeight: '20px',
  }),
}

const sectionGroupStyle = {
  gap: '16px',
}

const checkboxRowsStyle = {
  gap: '12px',
}

const HeaderOffsetHeight = 76

const filterResetStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: '#C9A6FF',
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const categorySelectStyle = selectInvertedStyle

const categorySelectOptionStyle = {
  backgroundColor: '#1F1F1F',
  color: '#FEFEFE',
}

const HomeGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path
      d="M1.5 6.8C1.5 6.03 1.86 5.3 2.47 4.82L5.91 2.1C6.82 1.38 8.1 1.38 9 2.1L12.44 4.82C13.05 5.3 13.41 6.03 13.41 6.8V10.9C13.41 12.28 12.29 13.4 10.91 13.4H9.97C9.62 13.4 9.34 13.12 9.34 12.78V10.9C9.34 10.21 8.79 9.65 8.09 9.65H6.84C6.14 9.65 5.59 10.21 5.59 10.9V12.78C5.59 13.12 5.31 13.4 4.97 13.4H4.03C2.65 13.4 1.53 12.28 1.53 10.9L1.5 6.8Z"
      stroke="#FEFEFE"
      strokeWidth="1.1"
    />
  </svg>
)

const BookmarkGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path
      d="M3.13 4.38C3.13 2.99 4.24 1.88 5.63 1.88H9.38C10.76 1.88 11.88 2.99 11.88 4.38V12.61C11.88 13.1 11.33 13.4 10.91 13.13L8.18 11.37C7.76 11.11 7.24 11.11 6.82 11.37L4.09 13.13C3.67 13.4 3.13 13.1 3.13 12.61V4.38Z"
      stroke="#FEFEFE"
      strokeWidth="0.94"
    />
  </svg>
)

const UsersGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <circle
      cx="2.12766"
      cy="2.12766"
      r="2.12766"
      transform="matrix(-1 0 0 1 9.76172 3.125)"
      stroke="#FEFEFE"
      strokeWidth="0.9375"
    />
    <path
      d="M3.91016 10.5372C3.91016 10.0796 4.19784 9.67138 4.62882 9.51746C6.57188 8.82351 8.69524 8.82351 10.6383 9.51746C11.0693 9.67138 11.357 10.0796 11.357 10.5372V11.237C11.357 11.8686 10.7975 12.3538 10.1723 12.2645L9.96381 12.2347C8.41816 12.0139 6.84896 12.0139 5.30331 12.2347L5.09484 12.2645C4.46957 12.3538 3.91016 11.8686 3.91016 11.237V10.5372Z"
      stroke="#FEFEFE"
      strokeWidth="0.9375"
    />
    <path
      d="M10.8244 7.43917C11.7476 7.43917 12.4961 6.69071 12.4961 5.76744C12.4961 4.84416 11.7476 4.0957 10.8244 4.0957"
      stroke="#FEFEFE"
      strokeWidth="0.9375"
      strokeLinecap="round"
    />
    <path
      d="M12.6554 11.253L12.8192 11.2764C13.3105 11.3466 13.75 10.9653 13.75 10.4691V9.91928C13.75 9.55971 13.524 9.23895 13.1854 9.11802C12.8476 8.99738 12.5029 8.90344 12.1543 8.83618"
      stroke="#FEFEFE"
      strokeWidth="0.9375"
      strokeLinecap="round"
    />
    <path
      d="M4.17564 7.43917C3.25237 7.43917 2.50391 6.69071 2.50391 5.76744C2.50391 4.84416 3.25237 4.0957 4.17564 4.0957"
      stroke="#FEFEFE"
      strokeWidth="0.9375"
      strokeLinecap="round"
    />
    <path
      d="M2.34458 11.253L2.18078 11.2764C1.6895 11.3466 1.24996 10.9653 1.24996 10.4691V9.91928C1.24996 9.55971 1.476 9.23895 1.81462 9.11802C2.15241 8.99738 2.49712 8.90344 2.8457 8.83618"
      stroke="#FEFEFE"
      strokeWidth="0.9375"
      strokeLinecap="round"
    />
  </svg>
)

const CardGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <rect x="1.25" y="2.5" width="12.5" height="10" rx="2.5" stroke="#FEFEFE" strokeWidth="1.1" />
    <path d="M1.81 5.63H13.19" stroke="#FEFEFE" strokeWidth="1.1" />
  </svg>
)

const AdminGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <rect x="1.8" y="2.1" width="11.4" height="10.8" rx="2.1" stroke="#FEFEFE" strokeWidth="1.05" />
    <path d="M4.35 5.05H10.65" stroke="#FEFEFE" strokeWidth="1.05" strokeLinecap="round" />
    <path d="M4.35 7.5H10.65" stroke="#FEFEFE" strokeWidth="1.05" strokeLinecap="round" />
    <path d="M4.35 9.95H8.55" stroke="#FEFEFE" strokeWidth="1.05" strokeLinecap="round" />
  </svg>
)

const ShieldGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path
      d="M7.5 1.9L11.8 3.55V6.75C11.8 9.38 10.08 11.76 7.5 12.92C4.92 11.76 3.2 9.38 3.2 6.75V3.55L7.5 1.9Z"
      stroke="#FEFEFE"
      strokeWidth="1.05"
      strokeLinejoin="round"
    />
    <path
      d="M5.6 7.42L6.86 8.68L9.5 6.03"
      stroke="#FEFEFE"
      strokeWidth="1.05"
      strokeLinecap="round"
    />
  </svg>
)

const HelpGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <circle cx="7.5" cy="7.5" r="6.3" stroke="#FEFEFE" strokeWidth="1.1" />
    <path
      d="M5.93 5.74C5.93 4.93 6.62 4.28 7.46 4.28C8.31 4.28 8.99 4.93 8.99 5.74C8.99 6.29 8.69 6.67 8.2 6.94C7.82 7.15 7.54 7.47 7.54 7.88V8.16"
      stroke="#FEFEFE"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
    <circle cx="7.54" cy="10.57" r="0.63" fill="#FEFEFE" />
  </svg>
)

const LogoutGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M10 6V5C10 4.45 10.45 4 11 4H18C18.55 4 19 4.45 19 5V19C19 19.55 18.55 20 18 20H11C10.45 20 10 19.55 10 19V18"
      stroke="#FEFEFE"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path d="M5 12H14" stroke="#FEFEFE" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M11 9L14 12L11 15"
      stroke="#FEFEFE"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const navigationItems = [
  { id: 'home', icon: <HomeGlyph />, label: 'Главная', kind: 'navigation' as const },
  { id: 'bookmarks', icon: <BookmarkGlyph />, label: 'Избранное', kind: 'navigation' as const },
  {
    id: 'community',
    icon: <UsersGlyph />,
    label: 'Комьюнити',
    kind: 'link' as const,
    href: 'https://t.me/epicgrowth_com_bot',
  },
  {
    id: 'subscription',
    icon: <CardGlyph />,
    label: 'Моя подписка',
    kind: 'link' as const,
    href: routes.catalogSubscription,
  },
  {
    id: 'support',
    icon: <HelpGlyph />,
    label: 'Поддержка',
    kind: 'action' as const,
    // Виджет чата снят; поддержку ведёт тот же бот, что в письмах и мини-аппе.
    action: openSupportChat,
  },
] as const

const fallbackFilterSections: FilterSection[] = [
  {
    id: 'format',
    title: 'Формат',
    options: [
      { id: 'case', label: 'Кейс', checked: false },
      { id: 'course', label: 'Курс', checked: false },
      { id: 'discussion', label: 'Дискуссия', checked: false },
      { id: 'workshop', label: 'Воркшоп', checked: false },
    ],
  },
]

type FilterSectionBlockProps = {
  section: FilterSection
  checkedState: Record<string, boolean>
  onOptionChange: (id: string, checked: boolean) => void
}

function FilterSectionBlock({ section, checkedState, onOptionChange }: FilterSectionBlockProps) {
  return (
    <section className="flex w-full flex-col" style={sectionGroupStyle}>
      <h3 style={sectionTitleStyle}>{section.title}</h3>
      <div className="flex w-full flex-col" style={checkboxRowsStyle}>
        {section.options.map((option) => (
          <div key={option.id} className="flex w-full items-start gap-2">
            <Checkbox
              checked={Boolean(checkedState[option.id])}
              onChange={(event) => onOptionChange(option.id, event.target.checked)}
              variant="videoFilterDark"
              label={option.label}
              className="min-w-0 flex-1"
              labelClassName="whitespace-pre-line break-words"
              aria-label={option.label}
            />
            {option.count ? (
              <span className="shrink-0 pt-px" style={optionCountStyle}>
                {option.count}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {section.footerLabel ? (
        <button type="button" className="cursor-pointer" style={optionCountStyle}>
          {section.footerLabel}
        </button>
      ) : null}
    </section>
  )
}

const resolveEffectiveSelection = (section: VideosShellSidebarFilterSection) => {
  return new Set(section.selectedOptionIds)
}

const arraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}

const FILTER_SELECTION_DEBOUNCE_MS = 420
const FILTER_SCROLL_RESTORE_MS = FILTER_SELECTION_DEBOUNCE_MS + 1200

export function VideosShellSidebar({
  isDesktopViewport,
  isVisible,
  isExpanded,
  isFilterPanelOpen,
  isFilterOnlyVisible = false,
  showFilterPanel = true,
  activeNavigationItem = 'home',
  onNavigationSelect,
  currentPathname,
  shortcuts = [],
  categories,
  selectedCategorySlug,
  onCategorySelect,
  bundles,
  selectedBundleId,
  onBundleSelect,
  filterSections,
  onFilterSelectionChange,
  onResetFilters,
  onLogout,
}: VideosShellSidebarProps) {
  const navigate = useNavigate()
  const usingExternalFilters = Boolean(filterSections && onFilterSelectionChange)

  const localInitialCheckedState = useMemo<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        fallbackFilterSections.flatMap((section) =>
          section.options.map((option) => [option.id, Boolean(option.checked)])
        )
      ),
    []
  )

  const [localCheckedState, setLocalCheckedState] =
    useState<Record<string, boolean>>(localInitialCheckedState)
  const [optimisticSelections, setOptimisticSelections] = useState<Record<string, string[]>>({})
  const debounceTimerRef = useRef<number | null>(null)
  const pendingSelectionRef = useRef<Record<string, string[]>>({})
  const filterPanelScrollRef = useRef<HTMLDivElement | null>(null)
  const pendingFilterPanelScrollTopRef = useRef<number | null>(null)
  const filterPanelScrollRestoreTimerRef = useRef<number | null>(null)
  const backendSelections = useMemo<Record<string, string[]>>(() => {
    if (!filterSections || filterSections.length === 0) {
      return {}
    }

    return Object.fromEntries(
      filterSections.map((section) => {
        const orderedSelection = section.options
          .map((option) => option.id)
          .filter((optionId) => section.selectedOptionIds.includes(optionId))
        return [section.id, orderedSelection]
      })
    )
  }, [filterSections])

  const externalCheckedState = useMemo<Record<string, boolean>>(() => {
    if (!filterSections) {
      return {}
    }

    const entries = filterSections.flatMap((section) => {
      const allowedOptionIds = new Set(section.options.map((option) => option.id))
      const backendOrderedSelection = section.options
        .map((option) => option.id)
        .filter((optionId) => section.selectedOptionIds.includes(optionId))
      const optimistic = optimisticSelections[section.id]
      const optimisticSanitized =
        optimistic?.filter((optionId) => allowedOptionIds.has(optionId)) ?? null
      const effectiveSelection =
        optimisticSanitized && !arraysEqual(optimisticSanitized, backendOrderedSelection)
          ? optimisticSanitized
          : backendOrderedSelection
      const effective = new Set(effectiveSelection)
      return section.options.map((option) => [option.id, effective.has(option.id)] as const)
    })

    return Object.fromEntries(entries)
  }, [filterSections, optimisticSelections])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      if (filterPanelScrollRestoreTimerRef.current) {
        window.clearTimeout(filterPanelScrollRestoreTimerRef.current)
        filterPanelScrollRestoreTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    pendingSelectionRef.current = backendSelections
  }, [backendSelections])

  const renderedSections = useMemo<FilterSection[]>(() => {
    if (!usingExternalFilters || !filterSections) {
      return fallbackFilterSections
    }

    return filterSections.map((section) => ({
      id: section.id,
      title: section.title,
      options: section.options.map((option) => ({
        id: option.id,
        label: option.title,
        count: option.count,
      })),
    }))
  }, [filterSections, usingExternalFilters])

  const captureFilterPanelScrollTop = useCallback(() => {
    if (filterPanelScrollRef.current) {
      pendingFilterPanelScrollTopRef.current = filterPanelScrollRef.current.scrollTop
    }

    if (filterPanelScrollRestoreTimerRef.current) {
      window.clearTimeout(filterPanelScrollRestoreTimerRef.current)
    }

    filterPanelScrollRestoreTimerRef.current = window.setTimeout(() => {
      pendingFilterPanelScrollTopRef.current = null
      filterPanelScrollRestoreTimerRef.current = null
    }, FILTER_SCROLL_RESTORE_MS)
  }, [])

  const restoreFilterPanelScrollTop = useCallback(() => {
    const scrollTop = pendingFilterPanelScrollTopRef.current

    if (scrollTop === null || !filterPanelScrollRef.current) {
      return
    }

    filterPanelScrollRef.current.scrollTop = scrollTop
  }, [])

  const restoreFilterPanelScrollTopAfterRender = useCallback(() => {
    restoreFilterPanelScrollTop()
    window.setTimeout(restoreFilterPanelScrollTop, 0)
  }, [restoreFilterPanelScrollTop])

  const appliedFiltersSignature = useMemo(() => {
    const filterSignature = Object.entries(backendSelections)
      .sort(([leftId], [rightId]) => leftId.localeCompare(rightId))
      .map(([filterId, selectedIds]) => `${filterId}:${selectedIds.join(',')}`)
      .join('|')

    return [selectedCategorySlug ?? '', selectedBundleId ?? '', filterSignature].join('|')
  }, [backendSelections, selectedBundleId, selectedCategorySlug])

  useLayoutEffect(() => {
    if (!showFilterPanel || !isFilterPanelOpen) {
      return
    }

    restoreFilterPanelScrollTop()
    const timeoutId = window.setTimeout(restoreFilterPanelScrollTop, 0)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [appliedFiltersSignature, isFilterPanelOpen, restoreFilterPanelScrollTop, showFilterPanel])

  const handleOptionChange = (sectionId: string, optionId: string, checked: boolean) => {
    if (!usingExternalFilters || !filterSections || !onFilterSelectionChange) {
      setLocalCheckedState((current) => ({
        ...current,
        [optionId]: checked,
      }))
      return
    }

    const targetSection = filterSections.find((section) => section.id === sectionId)
    if (!targetSection) {
      return
    }

    const optimisticSelected = optimisticSelections[sectionId] ?? null
    const effectiveSelected = new Set(
      optimisticSelected ?? [...resolveEffectiveSelection(targetSection)]
    )
    const nextSelected = new Set(effectiveSelected)

    if (checked) {
      nextSelected.add(optionId)
    } else {
      nextSelected.delete(optionId)
    }

    const allOptionIds = targetSection.options.map((option) => option.id)

    const orderedSelection = allOptionIds.filter((id) => nextSelected.has(id))
    const currentOrderedSelection = allOptionIds.filter((id) => effectiveSelected.has(id))
    if (arraysEqual(currentOrderedSelection, orderedSelection)) {
      return
    }

    const allSelectedByFilterId = Object.fromEntries(
      filterSections.map((section) => {
        const allowedIds = new Set(section.options.map((option) => option.id))
        const backendOrderedSelection = section.options
          .map((option) => option.id)
          .filter((id) => section.selectedOptionIds.includes(id))

        if (section.id === sectionId) {
          return [section.id, orderedSelection]
        }

        const optimisticForSection =
          optimisticSelections[section.id]?.filter((id) => allowedIds.has(id)) ?? null
        return [section.id, optimisticForSection ?? backendOrderedSelection]
      })
    )

    pendingSelectionRef.current = allSelectedByFilterId

    captureFilterPanelScrollTop()

    setOptimisticSelections((current) => ({
      ...current,
      [sectionId]: orderedSelection,
    }))
    restoreFilterPanelScrollTopAfterRender()

    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = window.setTimeout(() => {
      onFilterSelectionChange({ ...pendingSelectionRef.current })
      debounceTimerRef.current = null
    }, FILTER_SELECTION_DEBOUNCE_MS)
  }

  const handleResetClick = () => {
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    pendingSelectionRef.current = {}
    setOptimisticSelections({})
    onResetFilters?.()
  }

  const activeCheckedState = usingExternalFilters ? externalCheckedState : localCheckedState
  const staticNavigationItems = navigationItems
  const shortcutNavigationItems = shortcuts.map((shortcut) => ({
    id: shortcut.id,
    icon: shortcut.id === 'admin' ? <AdminGlyph /> : <ShieldGlyph />,
    label: shortcut.id === 'admin' ? 'Админ' : 'Супер админ',
    kind: 'link' as const,
    href: shortcut.href,
    isActive: Boolean(currentPathname?.startsWith(shortcut.href)),
  }))
  const logoutNavigationItem =
    !isDesktopViewport && onLogout
      ? [
          {
            id: 'logout',
            icon: <LogoutGlyph />,
            label: 'Выход',
            kind: 'action' as const,
            action: onLogout,
          },
        ]
      : []
  const navigationItemsToRender = [
    ...staticNavigationItems,
    ...shortcutNavigationItems,
    ...logoutNavigationItem,
  ]
  const rootVisibilityClass = isDesktopViewport
    ? 'flex'
    : isVisible || isFilterOnlyVisible
      ? 'flex'
      : 'hidden'
  const rootPointerEventsClass =
    isDesktopViewport || isVisible || isFilterOnlyVisible
      ? 'pointer-events-auto'
      : 'pointer-events-none'
  const showRail = isDesktopViewport || isVisible
  const resolvedFilterPanelStyle = isDesktopViewport
    ? filterPanelStyle
    : {
        ...filterPanelStyle,
        width: `min(${sidebarTokens.panelWidth}, 100vw)`,
        minWidth: `min(${sidebarTokens.panelWidth}, 100vw)`,
        maxWidth: '100vw',
      }
  const resolvedPanelBodyStyle = isDesktopViewport
    ? panelBodyStyle
    : {
        ...panelBodyStyle,
        width: '100%',
        maxWidth: '100%',
      }

  return (
    <aside
      className={`fixed left-0 z-30 max-w-full overflow-visible ${rootVisibilityClass} ${rootPointerEventsClass}`}
      style={{ top: `${HeaderOffsetHeight}px`, height: `calc(100vh - ${HeaderOffsetHeight}px)` }}
    >
      {showRail ? (
        <div className="h-full shrink-0" style={isExpanded ? expandedRailStyle : railStyle}>
          <nav
            className={`flex flex-col ${isExpanded ? 'items-stretch' : 'items-center'}`}
            style={
              isExpanded
                ? isDesktopViewport
                  ? expandedRailNavStyle
                  : mobileExpandedRailNavStyle
                : railNavStyle
            }
            aria-label="Навигация по библиотеке"
          >
            {navigationItemsToRender.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-label={item.label}
                className={`inline-flex cursor-pointer ${
                  isExpanded ? 'items-center justify-start px-3' : 'items-center justify-center'
                }`}
                style={{
                  ...(isExpanded
                    ? isDesktopViewport
                      ? expandedRailButtonStyle
                      : mobileExpandedRailButtonStyle
                    : railButtonStyle),
                  backgroundColor:
                    ('isActive' in item && item.isActive) || activeNavigationItem === item.id
                      ? sidebarTokens.railNavItemActiveBackground
                      : 'transparent',
                }}
                onClick={() => {
                  if (item.kind === 'navigation') {
                    if (item.id === 'home') {
                      handleResetClick()
                    }

                    onNavigationSelect?.(item.id)
                    if (!onNavigationSelect) {
                      navigate(item.id === 'bookmarks' ? '/catalog?favorites=1' : '/catalog')
                    }
                    return
                  }

                  if (item.kind === 'action') {
                    item.action()
                    return
                  }

                  if (item.href.startsWith('mailto:') || item.href.startsWith('http')) {
                    window.open(
                      item.href,
                      item.href.startsWith('mailto:') ? '_self' : '_blank',
                      'noopener,noreferrer'
                    )
                    return
                  }

                  navigate(item.href)
                }}
              >
                <span
                  className="inline-flex shrink-0 items-center justify-center"
                  style={{
                    width:
                      !isDesktopViewport && isExpanded ? '18px' : sidebarTokens.railIconSlotSize,
                    height:
                      !isDesktopViewport && isExpanded ? '18px' : sidebarTokens.railIconSlotSize,
                  }}
                >
                  {item.icon}
                </span>
                {isExpanded ? (
                  <span
                    className="ml-2 min-w-0 flex-1 truncate text-left"
                    style={!isDesktopViewport ? mobileNavLabelStyle : navLabelStyle}
                  >
                    {item.label}
                  </span>
                ) : null}
              </button>
            ))}
          </nav>
        </div>
      ) : null}
      {showFilterPanel && isFilterPanelOpen ? (
        <div
          ref={filterPanelScrollRef}
          data-testid="videos-shell-filter-panel"
          className="pointer-events-auto h-full overflow-x-hidden overflow-y-auto"
          style={resolvedFilterPanelStyle}
        >
          <div className="mt-4 flex w-full min-w-0 flex-col" style={resolvedPanelBodyStyle}>
            {onResetFilters ? (
              <button
                type="button"
                className="self-start cursor-pointer rounded-md border border-[#404040] px-3 py-1.5 hover:bg-[#2A2A2A]"
                style={filterResetStyle}
                onClick={handleResetClick}
              >
                Сбросить фильтры
              </button>
            ) : null}

            {categories && categories.length > 0 ? (
              <section className="flex w-full flex-col" style={sectionGroupStyle}>
                <h3 style={sectionTitleStyle}>Категория</h3>
                <Select
                  value={selectedCategorySlug ?? ''}
                  onChange={(event) => onCategorySelect?.(event.target.value || null)}
                  style={categorySelectStyle}
                  className="h-11 cursor-pointer"
                  aria-label="Выбор категории"
                >
                  <option value="" style={categorySelectOptionStyle}>
                    Все
                  </option>
                  {categories.map((category) => (
                    <option
                      key={category.id}
                      value={category.urlSlug}
                      style={categorySelectOptionStyle}
                    >
                      {category.title}
                    </option>
                  ))}
                </Select>
              </section>
            ) : null}

            {bundles && bundles.length > 0 ? (
              <section className="flex w-full flex-col" style={sectionGroupStyle}>
                <h3 style={sectionTitleStyle}>Подборка</h3>
                <Select
                  value={selectedBundleId ?? ''}
                  onChange={(event) => onBundleSelect?.(event.target.value || null)}
                  style={categorySelectStyle}
                  className="h-11 cursor-pointer"
                  aria-label="Выбор подборки"
                >
                  <option value="" style={categorySelectOptionStyle}>
                    Все
                  </option>
                  {bundles.map((bundle) => (
                    <option key={bundle.id} value={bundle.id} style={categorySelectOptionStyle}>
                      {bundle.title}
                    </option>
                  ))}
                </Select>
              </section>
            ) : null}

            {renderedSections.map((section) => (
              <FilterSectionBlock
                key={section.id}
                section={section}
                checkedState={activeCheckedState}
                onOptionChange={(optionId, checked) => {
                  handleOptionChange(section.id, optionId, checked)
                }}
              />
            ))}
          </div>
        </div>
      ) : null}
    </aside>
  )
}

VideosShellSidebar.displayName = 'VideosShellSidebar'
